import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot, type FirestoreError } from "firebase/firestore";
import { db } from "@/firebase";
import type { ProProfileDoc, UserDoc, WithId } from "@/types/firestore";
import { useAuthUser } from "@/hooks/useAuthUser";
import { validateLicenses, type LicenseDocument } from "@/lib/licenseValidation";

export type ProProfileState = {
  loading: boolean;
  proProfile: WithId<ProProfileDoc> | null;
  isVerified: boolean;
  isPending: boolean;
  hasActiveLicense: boolean;
  hasExpiringLicense: boolean;
  daysUntilExpiration: number | null;
  error: FirestoreError | null;
};

function resolveIsPro(userDoc: WithId<UserDoc> | null): boolean {
  if (!userDoc) {
    return false;
  }

  const topLevelFlag = userDoc.isPro === true;
  const roles = userDoc.roles ?? {};
  const asRecord = roles as Record<string, unknown>;

  return (
    topLevelFlag ||
    asRecord["isPro"] === true ||
    asRecord["pro"] === true ||
    asRecord["professional"] === true ||
    asRecord["attorney"] === true ||
    asRecord["paralegal"] === true
  );
}

function computeVerificationFlags(profile: WithId<ProProfileDoc> | null): { 
  isVerified: boolean; 
  isPending: boolean;
  hasActiveLicense: boolean;
  hasExpiringLicense: boolean;
  daysUntilExpiration: number | null;
} {
  if (!profile) {
    return { 
      isVerified: false, 
      isPending: false,
      hasActiveLicense: false,
      hasExpiringLicense: false,
      daysUntilExpiration: null,
    };
  }

  // Validate licenses
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const licenses = (profile.profile as any)?.licenses as LicenseDocument[] | undefined;
  const licenseValidation = validateLicenses(licenses);

  // Check admin verification status
  const verification = profile.verification;
  const status = verification?.status;
  const adminVerified = verification?.isVerified === true || status === "verified";
  
  // True verification requires BOTH admin approval AND active license
  const verified = adminVerified && licenseValidation.hasActiveLicense;
  
  const pendingStatuses = new Set([
    "pending",
    "pending_verification",
    "submitted",
    "none",
  ]);
  const pending = !verified && (status ? pendingStatuses.has(status) : true);

  return { 
    isVerified: verified, 
    isPending: pending,
    hasActiveLicense: licenseValidation.hasActiveLicense,
    hasExpiringLicense: licenseValidation.hasExpiringLicense,
    daysUntilExpiration: licenseValidation.daysUntilExpiration,
  };
}

export function useProProfile(): ProProfileState {
  const { status, firebaseUser, userDoc } = useAuthUser();
  const [state, setState] = useState<{
    loading: boolean;
    proProfile: WithId<ProProfileDoc> | null;
    error: FirestoreError | null;
  }>({ loading: false, proProfile: null, error: null });

  const isAuthenticated = status === "authed" && !!firebaseUser;
  const isPro = resolveIsPro(userDoc);

  useEffect(() => {
    if (!isAuthenticated || !firebaseUser || !isPro) {
      setState({ loading: false, proProfile: null, error: null });
      return undefined;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const profileRef = doc(db, "professionals", firebaseUser.uid);
    const unsubscribe = onSnapshot(
      profileRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setState({ loading: false, proProfile: null, error: null });
          return;
        }

        const data = snapshot.data() as ProProfileDoc;
        const nextProfile: WithId<ProProfileDoc> = { id: snapshot.id, ...data };
        setState({ loading: false, proProfile: nextProfile, error: null });
      },
      (error) => {
        console.error("[useProProfile] Failed to load professional profile", error);
        setState({ loading: false, proProfile: null, error });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [firebaseUser, isAuthenticated, isPro]);

  const verification = useMemo(() => computeVerificationFlags(state.proProfile), [state.proProfile]);

  const loading = status === "loading" || state.loading || (isAuthenticated && isPro && !state.proProfile && !state.error);

  return useMemo(
    () => ({
      loading,
      proProfile: state.proProfile,
      isVerified: verification.isVerified,
      isPending: verification.isPending,
      hasActiveLicense: verification.hasActiveLicense,
      hasExpiringLicense: verification.hasExpiringLicense,
      daysUntilExpiration: verification.daysUntilExpiration,
      error: state.error,
    }),
    [loading, state.proProfile, verification.isPending, verification.isVerified, verification.hasActiveLicense, verification.hasExpiringLicense, verification.daysUntilExpiration, state.error]
  );
}
